import {
  clearSummaryBarFields,
  setDefaultPreferences,
  updateColumnForDetail,
  updateColumnForDetailWidth,
  updateSummaryBarFields,
} from "../slice/authSlice";
import { authOnSuccess, authenticationFailed, logOutUser } from "../slice/utils";
import { apiSlice } from "./apiSlice";

/**
 * Auth API Slice
 *
 * RTK Query endpoints for authentication, session management, and user
 * onboarding flows. Includes auto-login, credential-based login, Google SSO,
 * invitation-based signup, password management, and OTP verification.
 */
export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    /**
     * Auto-login — silently re-authenticates the user using existing
     * session cookies on page load. On success, hydrates the auth state
     * and fetches default UI preferences.
     */
    autoLogin: builder.query({
      query: () => "/api_user/autologin/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(authOnSuccess(result.data));
            dispatch(authApiSlice.endpoints.getDefaultPreference.initiate());
          })
          .catch(() => dispatch(authenticationFailed()));
      },
    }),

    /** Update the user's password using a token-validated reset flow. */
    createPassword: builder.mutation({
      query: data => ({ url: "/user/create_pass/", method: "PATCH", body: data }),
    }),

    /** Request a password reset email for the given user credentials. */
    forgotPassword: builder.mutation({
      query: data => ({ url: "/api_user/forgot_password/", method: "POST", body: data }),
    }),

    /**
     * Fetch the user's persisted UI preferences (column visibility,
     * default views, etc.) from the backend. Dispatches
     * ``setDefaultPreferences`` on success.
     */
    getDefaultPreference: builder.query({
      query: () => `/api_user/miscellaneous/get_default_preference/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setDefaultPreferences(result.data)))
          .catch(error => {});
      },
      providesTags: _ => ["currentViewAndDetails"],
    }),

    /**
     * Google SSO login — authenticates via a Google ID token.
     * On success, fetches default preferences. On failure, dispatches
     * ``authenticationFailed`` to reset auth state.
     */
    googleLogin: builder.mutation({
      query: credentials => ({ url: "/user/google/", method: "POST", body: credentials }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(authApiSlice.endpoints.getDefaultPreference.initiate());
          })
          .catch(() => dispatch(authenticationFailed()));
      },
    }),

    /**
     * Credential-based login — authenticates with email and password.
     * If a ``VALID_USER`` code is returned (e.g., 2FA required), preferences
     * are not fetched yet; otherwise, they are loaded immediately.
     */
    login: builder.mutation({
      query: data => ({ url: "/api_user/login/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            if (result?.data?.code === "VALID_USER") return;
            dispatch(authApiSlice.endpoints.getDefaultPreference.initiate());
          })
          .catch(() => dispatch(authenticationFailed()));
      },
    }),

    /** Invalidate the current session and clear auth cookies. */
    logout: builder.mutation({
      query: data => ({ url: "/api_user/logout/", method: "POST", body: data }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (_) {
          // Access may already be expired; still clear local session below.
        } finally {
          dispatch(logOutUser());
        }
      },
    }),

    /**
     * Persist column visibility preferences for detail views.
     * Optimistically updates the Redux store via ``updateColumnForDetail``.
     */
    mutateColumnForDetail: builder.mutation({
      query: data => ({
        url: `/api_user/miscellaneous/set_default_preference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(updateColumnForDetail(data))).catch(error => {});
      },
    }),

    /**
     * Persist column width preferences for detail views.
     * Optimistically updates the Redux store via ``updateColumnForDetailWidth``.
     */
    mutateColumnForDetailWidth: builder.mutation({
      query: data => ({
        url: `/api_user/miscellaneous/set_default_preference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateColumnForDetailWidth(data)))
          .catch(error => {});
      },
    }),

    /**
     * Persist summary bar field visibility and order for detail pages.
     */
    mutateSummaryBarFields: builder.mutation({
      query: data => ({
        url: `/api_user/miscellaneous/set_default_preference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(() => dispatch(updateSummaryBarFields(data)))
          .catch(() => {});
      },
    }),

    /** Reset summary bar fields to catalogue default (clears saved preference). */
    resetSummaryBarFields: builder.mutation({
      query: data => ({
        url: `/api_user/miscellaneous/set_default_preference/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(() => dispatch(clearSummaryBarFields({ module: data.module })))
          .catch(() => {});
      },
    }),

    /** Reset the user's password using a backend-generated reset token. */
    resetPassword: builder.mutation({
      query: data => ({ url: "/api_user/reset_password/", method: "POST", body: data }),
    }),

    /** Send a one-time password (OTP) for two-factor authentication. */
    sendOTP: builder.mutation({
      query: data => ({ url: "/api_affirm/otp/", method: "POST", body: data }),
    }),

    /**
     * Invitation-based signup — registers a new user account using an
     * invitation token along with first name, last name, and password.
     *
     * BUSINESS RULE: Registration is invitation-only. The backend validates
     * the token for existence, expiry, and usage status before creating the
     * account.
     *
     * @endpoint POST /api_user/v2/user/signup/
     */
    signup: builder.mutation({
      query: credentials => ({
        url: "/api_user/v2/user/signup/",
        method: "POST",
        body: credentials,
      }),
    }),

    /** Update the user's password (requires current password verification). */
    updatePassword: builder.mutation({
      query: data => ({ url: "/user/update_pass/", method: "PATCH", body: data }),
    }),

    /**
     * Validate an invitation token before showing the signup form.
     *
     * BUSINESS RULE: This is a public endpoint (no auth required) that
     * verifies the token is valid, not expired, and still in PENDING status.
     * Throttled to 5 requests/minute per IP on the backend.
     *
     * @endpoint GET /api_user/v2/invite/validate_token/{token}/
     */
    validateToken: builder.query({
      query: token => `/api_user/v2/invite/validate_token/${token}/`,
    }),

    /**
     * Verify a one-time password for two-factor authentication.
     *
     * @param {object} params
     * @param {string} params.id - The OTP record ID.
     * @param {string} params.otp - The OTP code entered by the user.
     */
    verifyOTP: builder.mutation({
      query: ({ id, ...restData }) => ({
        url: `/api_affirm/otp/${id}/verify_otp/`,
        method: "POST",
        body: restData,
      }),
    }),

    /** Shopify-gated self-signup: signed form timestamp + Turnstile site key. */
    getSignupMeta: builder.query({
      query: () => "/api/user/v1/signup/meta/",
    }),

    /** Start Shopify-gated signup; emails a verification link. */
    startSignup: builder.mutation({
      query: data => ({
        url: "/api/user/v1/signup/start/",
        method: "POST",
        body: data,
      }),
    }),

    /** Verify signup email token; sets auth cookies on success. */
    verifySignup: builder.mutation({
      query: data => ({
        url: "/api/user/v1/signup/verify/",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useAutoLoginQuery,
  useCreatePasswordMutation,
  useForgotPasswordMutation,
  useGetDefaultPreferenceQuery,
  useGoogleLoginMutation,
  useLoginMutation,
  useLogoutMutation,
  useMutateColumnForDetailMutation,
  useMutateColumnForDetailWidthMutation,
  useMutateSummaryBarFieldsMutation,
  useResetSummaryBarFieldsMutation,
  useResetPasswordMutation,
  useSendOTPMutation,
  useSignupMutation,
  useUpdatePasswordMutation,
  useValidateTokenQuery,
  useVerifyOTPMutation,
  useGetSignupMetaQuery,
  useStartSignupMutation,
  useVerifySignupMutation,
} = authApiSlice;
